import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Home, ShoppingBag, Sparkles, Info, User, X } from 'lucide-react';
import { useApp } from '@/context/AppContext';
import { Button } from '@/components/ui/button';

const MobileNav = () => {
  const { isMenuOpen, setIsMenuOpen } = useApp();
  const location = useLocation();

  const navLinks = [
    { path: '/', label: 'Home', icon: Home },
    { path: '/products', label: 'Shop', icon: ShoppingBag },
    { path: '/customize', label: 'Customize', icon: Sparkles },
    { path: '/about', label: 'About', icon: Info },
    { path: '/auth', label: 'Account', icon: User },
  ];

  const handleLinkClick = () => {
    setIsMenuOpen(false);
  };

  return (
    <AnimatePresence>
      {isMenuOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-40 bg-background/80 backdrop-blur-sm md:hidden"
            onClick={() => setIsMenuOpen(false)}
          />

          {/* Slide-in Menu */}
          <motion.nav
            initial={{ x: '-100%', opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: '-100%', opacity: 0 }}
            transition={{ type: 'spring', stiffness: 300, damping: 30 }}
            className="fixed top-0 left-0 z-50 h-full w-72 bg-gradient-burgundy-soft border-r border-border/50 md:hidden overflow-y-auto"
          >
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-border/30">
              <div>
                <span className="font-display text-xl font-semibold text-silver-light">
                  Fazila
                </span>
                <span className="font-display text-xl font-light text-ivory ml-1">
                  Creations
                </span>
              </div>
              <Button
                variant="ghost"
                size="icon-sm"
                onClick={() => setIsMenuOpen(false)}
                className="text-silver hover:text-silver-light"
              >
                <X className="h-5 w-5" />
              </Button>
            </div>

            {/* Navigation Links */}
            <div className="p-4 space-y-2">
              {navLinks.map((link, index) => {
                const Icon = link.icon;
                const isActive = location.pathname === link.path;

                return (
                  <motion.div
                    key={link.path}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.05 }}
                  >
                    <Link
                      to={link.path}
                      onClick={handleLinkClick}
                      className={`flex items-center gap-4 px-4 py-3 rounded-lg transition-all duration-300 ${
                        isActive
                          ? 'bg-primary-light/20 text-silver-light border border-silver/20'
                          : 'text-muted-foreground hover:bg-muted/50 hover:text-silver'
                      }`}
                    >
                      <Icon className="h-5 w-5" />
                      <span className="font-sans font-medium">{link.label}</span>
                    </Link>
                  </motion.div>
                );
              })}
            </div>

            {/* Decorative Footer */}
            <div className="absolute bottom-0 left-0 right-0 p-6 border-t border-border/30">
              <p className="text-xs text-muted-foreground text-center font-body italic">
                "Made with love, care, and barakah"
              </p>
            </div>
          </motion.nav>
        </>
      )}
    </AnimatePresence>
  );
};

export default MobileNav;
